import { isValidDayStyle, type DayStyle } from "./day-styles";
import type { SavedRoute } from "./saved-route";

export const ROUTE_OUTCOME_VERSION = 1 as const;
export const ROUTE_OUTCOME_REASONS = [
  ["great-flow", "Enjoyed the flow"],
  ["good-variety", "Good variety"],
  ["too-hard", "Too hard"],
  ["too-easy", "Too easy"],
  ["too-much-climbing", "Too much climbing"],
  ["too-much-walking", "Too much walking"],
  ["too-much-waiting", "Too much waiting"],
  ["day-too-long", "Day felt too long"],
  ["conditions-worse", "Conditions were worse"],
  ["mapping-issue", "Map issue"],
] as const;
export type RouteOutcomeReason = (typeof ROUTE_OUTCOME_REASONS)[number][0];
export type RouteAgainAnswer = "yes" | "maybe" | "no";
export type RouteTimingSource = "explicit-start-completion" | "rider-reported" | "none";
export type RouteOutcomeRecord = {
  version: typeof ROUTE_OUTCOME_VERSION;
  journeyId: string;
  reportedAt: string;
  revision: number;
  slug: string;
  sport: "ski" | "mtb";
  dayStyle: DayStyle;
  routeDate: string;
  assessmentVersion: number | null;
  journeyScope: "selected-route" | "remaining-route";
  selectedRoute: "A" | "B";
  wouldRideAgain: RouteAgainAnswer;
  reasons: RouteOutcomeReason[];
  plannedRouteMinutes: number | null;
  requestedWindowMinutes: number | null;
  actualMinutes: number | null;
  timingSource: RouteTimingSource;
  timingComparable: boolean;
  meanTrailConditionScore: number | null;
  evidence: {
    confidence: "high" | "medium" | "low";
    preview: boolean;
    mapAccessCheckedAt: string | null;
    oldestLiftStatusAt: string | null;
    generatedAt: string | null;
  };
};

const KEY = "peakwave:route-outcomes:v1";
export const ROUTE_OUTCOMES_CHANGED = "peakwave-route-outcomes-changed";
export const MAX_ROUTE_OUTCOMES = 100;
export const MAX_ROUTE_OUTCOME_BYTES = 256 * 1024;
export const ROUTE_OUTCOME_RETENTION_MS = 365 * 86_400_000;
const reasons = new Set<string>(ROUTE_OUTCOME_REASONS.map(([key]) => key));
const finite = (value: unknown, min: number, max: number): value is number => typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
const instant = (value: unknown): value is string => typeof value === "string" && value.length <= 32 && Number.isFinite(Date.parse(value));
const nullableInstant = (value: unknown): value is string | null => value === null || instant(value);
const nullableNumber = (value: unknown, min: number, max: number): value is number | null => value === null || finite(value, min, max);
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);

export function isRouteJourneyId(value: unknown): value is string {
  return typeof value === "string" && /^[a-zA-Z0-9_-]{8,80}$/.test(value);
}

/** A legacy saved route gets a stable local identity without sending its geometry. */
export function routeJourneyId(route: SavedRoute): string {
  if (isRouteJourneyId(route.journeyId)) return route.journeyId;
  const input = `${route.slug}|${route.savedAt}|${route.plan.generatedAt ?? ""}|${route.plan.selectedRoute ?? "A"}`;
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) hash = Math.imul(hash ^ input.charCodeAt(i), 16777619);
  return `legacy-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

/** Rebuild the allowlisted shape; malformed or extra input never reaches exports or analytics. */
export function parseRouteOutcome(raw: unknown): RouteOutcomeRecord | null {
  if (!object(raw) || raw.version !== ROUTE_OUTCOME_VERSION || !isRouteJourneyId(raw.journeyId)
    || !instant(raw.reportedAt) || !finite(raw.revision, 1, 10_000) || !Number.isInteger(raw.revision)
    || typeof raw.slug !== "string" || raw.slug.length > 100 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(raw.slug)
    || (raw.sport !== "ski" && raw.sport !== "mtb") || typeof raw.dayStyle !== "string" || !isValidDayStyle(raw.sport, raw.dayStyle)
    || typeof raw.routeDate !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(raw.routeDate) || !Number.isFinite(Date.parse(raw.routeDate)) || new Date(raw.routeDate).toISOString().slice(0, 10) !== raw.routeDate
    || !nullableNumber(raw.assessmentVersion, 1, 10_000) || (raw.assessmentVersion !== null && !Number.isInteger(raw.assessmentVersion))
    || (raw.journeyScope !== "selected-route" && raw.journeyScope !== "remaining-route")
    || (raw.selectedRoute !== "A" && raw.selectedRoute !== "B")
    || (raw.wouldRideAgain !== "yes" && raw.wouldRideAgain !== "maybe" && raw.wouldRideAgain !== "no")
    || !Array.isArray(raw.reasons) || raw.reasons.length > 3 || !raw.reasons.every((reason) => typeof reason === "string" && reasons.has(reason))
    || new Set(raw.reasons).size !== raw.reasons.length
    || !nullableNumber(raw.plannedRouteMinutes, 1, 1440) || !nullableNumber(raw.requestedWindowMinutes, 1, 1440)
    || !nullableNumber(raw.actualMinutes, 1, 1440) || typeof raw.timingComparable !== "boolean"
    || (raw.timingSource !== "none" && raw.timingSource !== "rider-reported" && raw.timingSource !== "explicit-start-completion")
    || (raw.timingSource === "none") !== (raw.actualMinutes === null)
    || (raw.timingComparable && (raw.actualMinutes === null || raw.plannedRouteMinutes === null))
    || !nullableNumber(raw.meanTrailConditionScore, 0, 10) || !object(raw.evidence)) return null;
  const evidence = raw.evidence;
  if ((evidence.confidence !== "high" && evidence.confidence !== "medium" && evidence.confidence !== "low")
    || typeof evidence.preview !== "boolean" || !nullableInstant(evidence.mapAccessCheckedAt)
    || !nullableInstant(evidence.oldestLiftStatusAt) || !nullableInstant(evidence.generatedAt)) return null;
  return {
    version: ROUTE_OUTCOME_VERSION, journeyId: raw.journeyId, reportedAt: raw.reportedAt, revision: raw.revision,
    slug: raw.slug, sport: raw.sport, dayStyle: raw.dayStyle as DayStyle, routeDate: raw.routeDate,
    assessmentVersion: raw.assessmentVersion, journeyScope: raw.journeyScope, selectedRoute: raw.selectedRoute,
    wouldRideAgain: raw.wouldRideAgain, reasons: raw.reasons as RouteOutcomeReason[], plannedRouteMinutes: raw.plannedRouteMinutes,
    requestedWindowMinutes: raw.requestedWindowMinutes, actualMinutes: raw.actualMinutes, timingSource: raw.timingSource,
    timingComparable: raw.timingComparable, meanTrailConditionScore: raw.meanTrailConditionScore,
    evidence: { confidence: evidence.confidence, preview: evidence.preview, mapAccessCheckedAt: evidence.mapAccessCheckedAt, oldestLiftStatusAt: evidence.oldestLiftStatusAt, generatedAt: evidence.generatedAt },
  };
}

export function explicitRouteElapsedMinutes(route: SavedRoute): number | null {
  if (route.status !== "completed" || !route.startedAt || !route.completedAt) return null;
  const minutes = (Date.parse(route.completedAt) - Date.parse(route.startedAt)) / 60_000;
  return finite(minutes, 1, 1440) ? Math.round(minutes) : null;
}

const minutesOrNull = (value: number): number | null => finite(value, 1, 1440) ? Math.round(value) : null;
const hmMinutes = (value: string): number => /^\d{2}:\d{2}$/.test(value) ? Number(value.slice(0, 2)) * 60 + Number(value.slice(3)) : NaN;

export function buildRouteOutcome(route: SavedRoute, response: {
  wouldRideAgain: RouteAgainAnswer;
  reasons?: RouteOutcomeReason[];
  timing?: { source: "rider-reported"; minutes: number } | { source: "explicit-start-completion" };
}, now = new Date()): RouteOutcomeRecord | null {
  if (route.status !== "completed") return null;
  const legs = route.plan.itinerary.legs;
  const planned = minutesOrNull(((legs.at(-1)?.arriveHour ?? NaN) - (legs[0]?.departHour ?? NaN)) * 60);
  const timing = response.timing;
  const actual = timing?.source === "rider-reported" ? minutesOrNull(timing.minutes) : timing ? explicitRouteElapsedMinutes(route) : null;
  if (timing && actual === null) return null;
  const scored = legs.flatMap((leg) => leg.kind === "trail" && !leg.climb && leg.trail?.score != null && finite(leg.trail.score, 0, 10)
    ? [{ minutes: Math.max(0, (leg.arriveHour - leg.departHour) * 60), score: leg.trail.score }] : []);
  const scoredMinutes = scored.reduce((total, leg) => total + leg.minutes, 0);
  return parseRouteOutcome({
    version: ROUTE_OUTCOME_VERSION, journeyId: routeJourneyId(route), reportedAt: now.toISOString(), revision: (route.outcome?.revision ?? 0) + 1,
    slug: route.slug, sport: route.preferences.sport, dayStyle: route.preferences.dayStyle, routeDate: route.plan.scoredDate,
    assessmentVersion: route.plan.assessmentVersion ?? null, journeyScope: route.journeyScope ?? "selected-route", selectedRoute: route.plan.selectedRoute ?? "A",
    wouldRideAgain: response.wouldRideAgain, reasons: response.reasons ?? [], plannedRouteMinutes: planned,
    requestedWindowMinutes: minutesOrNull(hmMinutes(route.preferences.endTime) - hmMinutes(route.preferences.startTime)),
    actualMinutes: actual, timingSource: timing?.source ?? "none",
    timingComparable: actual !== null && planned !== null && (timing?.source === "rider-reported" || route.startedProgress === 0),
    meanTrailConditionScore: scoredMinutes > 0 ? Math.round(scored.reduce((total, leg) => total + leg.minutes * leg.score, 0) / scoredMinutes * 10) / 10 : null,
    evidence: { confidence: route.plan.routeExplanation.confidence, preview: route.plan.preview === true,
      mapAccessCheckedAt: route.plan.evidence?.mapAccessCheckedAt ?? null, oldestLiftStatusAt: route.plan.evidence?.oldestLiftStatusAt ?? null, generatedAt: route.plan.generatedAt ?? null },
  });
}

function retained(records: unknown[], now: Date): RouteOutcomeRecord[] {
  const latest = new Map<string, RouteOutcomeRecord>();
  for (const raw of records) {
    const record = parseRouteOutcome(raw);
    if (!record || Date.parse(record.reportedAt) < now.getTime() - ROUTE_OUTCOME_RETENTION_MS || Date.parse(record.reportedAt) > now.getTime() + 300_000) continue;
    const previous = latest.get(record.journeyId);
    if (!previous || record.revision > previous.revision || (record.revision === previous.revision && record.reportedAt > previous.reportedAt)) latest.set(record.journeyId, record);
  }
  return [...latest.values()].sort((a, b) => Date.parse(b.reportedAt) - Date.parse(a.reportedAt)).slice(0, MAX_ROUTE_OUTCOMES);
}

export function readRouteOutcomes(now = new Date()): RouteOutcomeRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw || new TextEncoder().encode(raw).length > MAX_ROUTE_OUTCOME_BYTES) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!object(parsed) || parsed.version !== 1 || !Array.isArray(parsed.records) || parsed.records.length > MAX_ROUTE_OUTCOMES) return [];
    const records = retained(parsed.records, now);
    const clean = JSON.stringify({ version: ROUTE_OUTCOME_VERSION, records });
    if (clean !== raw) {
      try { localStorage.setItem(KEY, clean); } catch { /* Retained feedback remains readable if pruning storage fails. */ }
    }
    return records;
  } catch { return []; }
}

export function storeRouteOutcome(outcome: RouteOutcomeRecord, now = new Date()): boolean {
  try {
    const safe = parseRouteOutcome(outcome);
    if (!safe || Date.parse(safe.reportedAt) < now.getTime() - ROUTE_OUTCOME_RETENTION_MS || Date.parse(safe.reportedAt) > now.getTime() + 300_000) return false;
    const records = retained([...readRouteOutcomes(now), safe], now);
    let json = JSON.stringify({ version: ROUTE_OUTCOME_VERSION, records });
    while (new TextEncoder().encode(json).length > MAX_ROUTE_OUTCOME_BYTES && records.length > 1) {
      records.pop(); json = JSON.stringify({ version: ROUTE_OUTCOME_VERSION, records });
    }
    if (new TextEncoder().encode(json).length > MAX_ROUTE_OUTCOME_BYTES) return false;
    localStorage.setItem(KEY, json);
    if (typeof window !== "undefined") window.dispatchEvent(new Event(ROUTE_OUTCOMES_CHANGED));
    return true;
  } catch { return false; }
}

export function clearRouteOutcomes(): boolean {
  try {
    localStorage.removeItem(KEY);
    if (typeof window !== "undefined") window.dispatchEvent(new Event(ROUTE_OUTCOMES_CHANGED));
    return true;
  } catch { return false; }
}

export function exportRouteOutcomes(now = new Date()): string {
  return JSON.stringify({ version: ROUTE_OUTCOME_VERSION, exportedAt: now.toISOString(), records: readRouteOutcomes(now) }, null, 2);
}
